import { billingAccessBlockReason } from "../../supabase/functions/super-admin-api/billing-access";

export interface CurrentBillingAccess {
  organization_id: string | null;
  ai_active: boolean | null;
  state: string | null;
  reason: string | null;
  owed_total: number | null;
  prepaid_expires_on: string | null;
  due_cycles?: number | null;
  oldest_due_date?: string | null;
  grace_until?: string | null;
  suspended_at?: string | null;
}

export type PaywallLockReason =
  | "terminated"
  | "subscription_suspended"
  | "manual_block"
  | "authorization_lost"
  | "prepaid_expired"
  | "overdue"
  | "ai_inactive";

export type PaywallDecision =
  | { kind: "open" }
  | { kind: "loading" }
  | { kind: "locked"; reason: PaywallLockReason };

export interface PaywallInput {
  subscriptionStatus: string | null;
  brandId: string;
  organizationId: string | null;
  access: CurrentBillingAccess | null;
  accessLoading: boolean;
}

export function accessRuleApplies(subscriptionStatus: string | null, brandId: string): boolean {
  return subscriptionStatus === "suspended" && brandId === "flow";
}

export function parseCurrentBillingAccess(data: unknown): CurrentBillingAccess | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const row = data as Record<string, unknown>;
  const str = (value: unknown) => (typeof value === "string" ? value : null);
  const count = (value: unknown) => (value == null || !Number.isFinite(Number(value)) ? null : Number(value));
  return {
    organization_id: str(row.organization_id),
    ai_active: typeof row.ai_active === "boolean" ? row.ai_active : null,
    state: str(row.state),
    reason: str(row.reason),
    owed_total: row.owed_total == null || !Number.isFinite(Number(row.owed_total)) ? null : Number(row.owed_total),
    prepaid_expires_on: str(row.prepaid_expires_on),
    due_cycles: count(row.due_cycles),
    oldest_due_date: str(row.oldest_due_date),
    grace_until: str(row.grace_until),
    suspended_at: str(row.suspended_at),
  };
}

export function paywallDecision(input: PaywallInput): PaywallDecision {
  const { subscriptionStatus, brandId, organizationId, access, accessLoading } = input;
  if (subscriptionStatus === "terminated") return { kind: "locked", reason: "terminated" };
  if (subscriptionStatus !== "suspended") return { kind: "open" };
  if (!accessRuleApplies(subscriptionStatus, brandId)) return { kind: "locked", reason: "subscription_suspended" };
  if (accessLoading) return { kind: "loading" };
  if (!access || !organizationId || access.organization_id !== organizationId) {
    return { kind: "locked", reason: "subscription_suspended" };
  }

  const blocked = billingAccessBlockReason(access as unknown as Record<string, unknown>);
  if (blocked === "manual_block" || blocked === "authorization_lost" || blocked === "prepaid_expired" || blocked === "overdue") {
    return { kind: "locked", reason: blocked };
  }
  if (blocked) return { kind: "locked", reason: "subscription_suspended" };
  if (access.ai_active === false) return { kind: "locked", reason: "ai_inactive" };
  return { kind: "open" };
}
